import { History, LineChart, Pencil, Share2 } from "lucide-react";
import {
  useDelete,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";

import { ExternalAnchor } from "@/components/market-intel/components";
import { formatShortDate } from "@/components/market-intel/dates";
import { riseStyle } from "@/components/market-intel/util";
import { Button } from "@/components/ui/button";
import { toIntlLocale } from "@/lib/dates";
import { cn } from "@/lib/utils";

import { PlatformBadge } from "./PlatformIcon";
import { SnapshotHistory } from "./SnapshotHistory";
import { ProfileForm, SnapshotForm } from "./SocialForms";
import {
  snapshotsByProfile,
  sortProfiles,
  sparklinePoints,
  summarizeProfile,
  type ProfileSummary,
} from "./social";
import type {
  CompetitorSocialProfile,
  CompetitorSocialSnapshot,
} from "./types";
import { AddButton, ConfirmDeleteButton, IconAction, SectionCard } from "./ui";

type Dialog =
  | { kind: "profile"; profile: CompetitorSocialProfile | null }
  | { kind: "snapshot"; profile: CompetitorSocialProfile }
  | { kind: "history"; profile: CompetitorSocialProfile };

/** The competitor's social profiles, each with its latest measurement. */
export const SocialProfilesSection = ({
  id,
  competitorId,
}: {
  id: string;
  competitorId: Identifier;
}) => {
  const translate = useTranslate();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const { data: profiles } = useGetList<CompetitorSocialProfile>(
    "competitor_social_profiles",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
      filter: { competitor_id: competitorId },
    },
  );
  const profileIds = (profiles ?? []).map((profile) => profile.id);
  const { data: snapshots } = useGetList<CompetitorSocialSnapshot>(
    "competitor_social_snapshots",
    {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "measured_at", order: "ASC" },
      filter: { "profile_id@in": `(${profileIds.join(",")})` },
    },
    { enabled: profileIds.length > 0 },
  );
  const history = useMemo(
    () => snapshotsByProfile(snapshots ?? []),
    [snapshots],
  );
  const sorted = sortProfiles(profiles ?? []);
  const historyOf = (profile: CompetitorSocialProfile) =>
    history.get(String(profile.id)) ?? [];

  return (
    <SectionCard
      id={id}
      icon={Share2}
      title={translate("competition.social.heading")}
      count={sorted.length || undefined}
      style={riseStyle(2)}
      action={
        <AddButton
          label={translate("competition.social.add")}
          onClick={() => setDialog({ kind: "profile", profile: null })}
        />
      }
    >
      {sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("competition.social.empty")}
        </p>
      ) : (
        <ul className="-mx-1 divide-y divide-border/70">
          {sorted.map((profile) => (
            <ProfileRow
              key={profile.id}
              profile={profile}
              summary={summarizeProfile(historyOf(profile))}
              onEdit={() => setDialog({ kind: "profile", profile })}
              onMeasure={() => setDialog({ kind: "snapshot", profile })}
              onHistory={() => setDialog({ kind: "history", profile })}
            />
          ))}
        </ul>
      )}
      <ProfileForm
        open={dialog?.kind === "profile"}
        onOpenChange={(open) => !open && setDialog(null)}
        competitorId={competitorId}
        profile={dialog?.kind === "profile" ? dialog.profile : null}
      />
      <SnapshotForm
        open={dialog?.kind === "snapshot"}
        onOpenChange={(open) => !open && setDialog(null)}
        profile={dialog?.kind === "snapshot" ? dialog.profile : null}
        history={dialog?.kind === "snapshot" ? historyOf(dialog.profile) : []}
      />
      <SnapshotHistory
        open={dialog?.kind === "history"}
        onOpenChange={(open) => !open && setDialog(null)}
        profile={dialog?.kind === "history" ? dialog.profile : null}
        history={dialog?.kind === "history" ? historyOf(dialog.profile) : []}
      />
    </SectionCard>
  );
};

const ProfileRow = ({
  profile,
  summary,
  onEdit,
  onMeasure,
  onHistory,
}: {
  profile: CompetitorSocialProfile;
  summary: ProfileSummary;
  onEdit: () => void;
  onMeasure: () => void;
  onHistory: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const [remove, { isPending }] = useDelete<CompetitorSocialProfile>();
  const number = new Intl.NumberFormat(toIntlLocale(locale));
  const { latest, previous, deltas } = summary;

  const onDelete = () =>
    remove(
      "competitor_social_profiles",
      { id: profile.id, previousData: profile },
      {
        mutationMode: "optimistic",
        onSuccess: () => notify("competition.social.deleted", { type: "info" }),
        onError: () => notify("competition.form.error", { type: "error" }),
      },
    );

  return (
    <li
      className="min-w-0 px-1 py-3 first:pt-0 last:pb-0"
      data-platform={profile.platform}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <PlatformBadge platform={profile.platform} />
        <div className="min-w-0 flex-1">
          {profile.url ? (
            <ExternalAnchor href={profile.url} className="text-[15px]">
              {profile.handle}
            </ExternalAnchor>
          ) : (
            <p className="truncate text-[15px] font-medium">{profile.handle}</p>
          )}
          <p className="truncate text-xs text-muted-foreground">
            {latest
              ? translate("competition.social.measured_on", {
                  date: formatShortDate(latest.measured_at, locale),
                })
              : translate("competition.social.no_measurement")}
          </p>
        </div>
        <IconAction
          label={`${translate("competition.social.edit")}: ${profile.handle}`}
          icon={Pencil}
          onClick={onEdit}
        />
        <ConfirmDeleteButton
          label={`${translate("competition.social.delete")}: ${profile.handle}`}
          onConfirm={onDelete}
          disabled={isPending}
        />
      </div>
      {latest ? (
        <div className="mt-2.5 min-w-0">
          <dl className="grid min-w-0 grid-cols-3 gap-2">
            <Metric
              label={translate("competition.social.followers")}
              value={latest.followers}
              delta={deltas.followers}
              number={number}
              since={previous?.measured_at}
            />
            <Metric
              label={translate("competition.social.posts")}
              value={latest.posts_count}
              delta={deltas.posts_count}
              number={number}
              since={previous?.measured_at}
            />
            <Metric
              label={translate("competition.social.following")}
              value={latest.following_count}
              delta={deltas.following_count}
              number={number}
              since={previous?.measured_at}
            />
          </dl>
        </div>
      ) : null}
      {latest &&
      (latest.posts_per_week != null || latest.engagement_rate != null) ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {[
            latest.posts_per_week != null
              ? `${translate("competition.social.posts_per_week")}: ${number.format(latest.posts_per_week)}`
              : null,
            latest.engagement_rate != null
              ? `${translate("competition.social.engagement")}: ${number.format(latest.engagement_rate)} %`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}
      {profile.notes ? (
        <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">
          {profile.notes}
        </p>
      ) : null}
      <div className="mt-1.5 flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onMeasure}
          className="-ml-2 h-8 rounded-full px-2.5 text-xs font-semibold text-primary"
        >
          <LineChart aria-hidden className="size-3.5" />
          {translate("competition.social.add_measurement")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onHistory}
          className="h-8 rounded-full px-2.5 text-xs font-semibold text-muted-foreground"
        >
          <History aria-hidden className="size-3.5" />
          {translate("competition.social.history")}
        </Button>
        <span className="flex-1" />
        <Sparkline series={summary.followersSeries} number={number} />
      </div>
    </li>
  );
};

const Metric = ({
  label,
  value,
  delta,
  number,
  since,
}: {
  label: string;
  value: number | null | undefined;
  delta: number | null;
  number: Intl.NumberFormat;
  since?: string;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const date = since ? formatShortDate(since, locale) : "";
  const deltaText =
    delta == null || !since
      ? null
      : delta === 0
        ? translate("competition.social.delta_flat", { date })
        : translate(
            delta > 0
              ? "competition.social.delta_up"
              : "competition.social.delta_down",
            { value: number.format(delta), date },
          );
  return (
    <div className="min-w-0 rounded-xl bg-muted/50 px-2.5 py-2 dark:bg-white/5">
      <dt className="truncate text-xs font-medium text-muted-foreground">
        {label}
      </dt>
      <dd className="text-lg leading-tight font-semibold tabular-nums">
        {value == null ? "—" : number.format(value)}
      </dd>
      {deltaText ? (
        <dd
          title={deltaText}
          className={cn(
            "truncate text-[11px] font-semibold tabular-nums",
            delta! > 0
              ? "text-emerald-700 dark:text-emerald-300"
              : delta! < 0
                ? "text-red-700 dark:text-red-300"
                : "text-muted-foreground",
          )}
        >
          <span aria-hidden>
            {delta! > 0 ? "▲ " : delta! < 0 ? "▼ " : ""}
            {delta === 0 ? "=" : number.format(Math.abs(delta!))}
          </span>
          <span className="sr-only">{deltaText}</span>
        </dd>
      ) : null}
    </div>
  );
};

const SPARK_W = 72;
const SPARK_H = 32;

/** Followers over time; hidden with fewer than two measurements. */
const Sparkline = ({
  series,
  number,
}: {
  series: { day: string; value: number }[];
  number: Intl.NumberFormat;
}) => {
  const translate = useTranslate();
  if (series.length < 2) return null;
  const points = sparklinePoints(
    series.map((point) => point.value),
    SPARK_W,
    SPARK_H,
  );
  const last = points.split(" ").pop()!.split(",");
  const rising = series[series.length - 1].value >= series[0].value;
  return (
    <svg
      role="img"
      aria-label={translate("competition.social.chart", {
        values: series.map((point) => number.format(point.value)).join(", "),
      })}
      width={SPARK_W}
      height={SPARK_H}
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      className={cn(
        "shrink-0",
        rising
          ? "text-emerald-600 dark:text-emerald-300"
          : "text-red-600 dark:text-red-300",
      )}
      data-testid="followers-sparkline"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r={2.5} fill="currentColor" />
    </svg>
  );
};
